import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Link } from "react-router-dom";
import Monthly_navbar from './Monthlytasks_navbar';
import moment from "moment";
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import { scrollToTop } from '../Shared/ScrollOnTop';
import config from '../../Config';
import FeePostingBusyOverlay from '../Shared/FeePostingBusyOverlay';

const CHALLAN_POST_TIMEOUT_MS = 20 * 60 * 1000;

const MONTH_OPTIONS = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
];

function defaultMonthString() {
    return String(new Date().getMonth() + 1);
}

function defaultYearString() {
    return moment().format('YYYY');
}

/** YYYY-MM-DD for date input — last calendar day of the given posting month/year. */
function endOfMonthForPosting(monthStr, yearStr) {
    const m = parseInt(monthStr, 10);
    const y = parseInt(yearStr, 10);
    if (!Number.isFinite(m) || !Number.isFinite(y) || m < 1 || m > 12) {
        return moment().endOf('month').format('YYYY-MM-DD');
    }
    return moment({ year: y, month: m - 1, date: 1 }).endOf('month').format('YYYY-MM-DD');
}

const StartNewMonthChallan = () => {
    document.title = "Start New Month (Challan)";
    const [Month, setMonth] = useState(() => defaultMonthString());
    const [Year, setYear] = useState(() => defaultYearString());
    const [dueDate, setDueDate] = useState(() => endOfMonthForPosting(defaultMonthString(), defaultYearString()));
    /** Same as legacy StartNewMonth.aspx chkTransport (default on): post 005 from bus routes (no concession on transport; tuition concession applies to fee code 013 only). */
    const [postTransportFee, setPostTransportFee] = useState(true);
    const [fYearList, setFYearList] = useState([]);
    const [fYearsLoaded, setFYearsLoaded] = useState(false);

    const [AccessKey] = useState(localStorage.getItem("AccessKey"));
    const [UserID] = useState(localStorage.getItem("ID"));
    const [MemberType] = useState(localStorage.getItem("MemberType"));
    const [Group_Name] = useState(localStorage.getItem("Group_Name"));
    const [SessionID] = useState(localStorage.getItem("DefaultSession"));

    const isAdministrator = Group_Name === "Administrator";

    const deletablePeriods = useMemo(() => {
        const cur = moment().startOf('month');
        const prev = moment().subtract(1, 'month').startOf('month');
        return [
            {
                key: cur.format('YYYY-MM'),
                month: cur.month() + 1,
                year: cur.year(),
                label: `Current month — ${cur.format('MMMM YYYY')}`,
            },
            {
                key: prev.format('YYYY-MM'),
                month: prev.month() + 1,
                year: prev.year(),
                label: `Previous month — ${prev.format('MMMM YYYY')}`,
            },
        ];
    }, []);

    const [deletePeriodKey, setDeletePeriodKey] = useState(() => moment().format('YYYY-MM'));

    /** null | 'challan' | 'delete' */
    const [busyMode, setBusyMode] = useState(null);
    const [busyTitle, setBusyTitle] = useState('');
    const [busyDetail, setBusyDetail] = useState('');
    const [elapsedSec, setElapsedSec] = useState(0);
    const busyTimerRef = useRef(null);

    const isBusy = busyMode !== null;

    const clearBusyTimer = () => {
        if (busyTimerRef.current) {
            clearInterval(busyTimerRef.current);
            busyTimerRef.current = null;
        }
    };

    const startBusyOverlay = (title, detail, mode) => {
        setBusyTitle(title);
        setBusyDetail(detail);
        setBusyMode(mode);
        setElapsedSec(0);
        clearBusyTimer();
        busyTimerRef.current = setInterval(() => setElapsedSec((n) => n + 1), 1000);
    };

    const stopBusyOverlay = () => {
        clearBusyTimer();
        setBusyMode(null);
        setElapsedSec(0);
    };

    useEffect(() => () => clearBusyTimer(), []);

    useEffect(() => {
        const curKey = moment().format('YYYY-MM');
        if (deletablePeriods.some((p) => p.key === curKey)) {
            setDeletePeriodKey(curKey);
        }
    }, [deletablePeriods]);

    useEffect(() => {
        const apiData = {
            AccessKey: AccessKey,
            UserID: UserID,
            MemberType: MemberType,
            PageNo: 1,
            PageSize: 100,
        };
        axios({
            method: 'post',
            url: config.base_url + 'Accounts/GetFYears',
            headers: { 'Content-Type': 'application/json' },
            data: apiData,
        })
            .then((response) => {
                const list = response.data?.ListofRecords || [];
                setFYearList(Array.isArray(list) ? list : []);
            })
            .catch(() => {
                setFYearList([]);
            })
            .finally(() => {
                setFYearsLoaded(true);
            });
    }, [AccessKey, UserID, MemberType]);

    useEffect(() => {
        if (!fYearList.length) return;
        const resolveYear = (prev) => {
            const p = String(prev || '');
            if (fYearList.some((item) => String(item.FYear) === p)) return p;
            const cur = defaultYearString();
            if (fYearList.some((item) => String(item.FYear) === cur)) return cur;
            return String(fYearList[0].FYear);
        };
        setYear((prev) => resolveYear(prev));
    }, [fYearList]);

    useEffect(() => {
        if (!Month || !Year) return;
        setDueDate(endOfMonthForPosting(Month, Year));
    }, [Month, Year]);

    const handleMonth = () => {
        if (!fYearsLoaded || !fYearList.length) {
            toast.error('Financial years are still loading. Please wait a moment.', {
                position: toast.POSITION.TOP_RIGHT,
            });
            return;
        }
        if (!Month || !Year || Month === '' || Year === '') {
            toast.error('Please select month and year.', { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        const sm = parseInt(Month, 10);
        const fy = parseInt(Year, 10);
        if (!Number.isFinite(sm) || !Number.isFinite(fy) || sm < 1 || sm > 12) {
            toast.error('Invalid month or year.', { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        if (!dueDate || !moment(dueDate, 'YYYY-MM-DD', true).isValid()) {
            toast.error('Please select a valid challan due date.', { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        const data = {
            AccessKey: AccessKey,
            UserID: UserID,
            MemberType: MemberType,
            SchoolID: 1,
            SelectedMonth: sm,
            FYear: fy,
            SessionID: SessionID,
            AddTransportFee: postTransportFee,
            ChallanDueDate: `${dueDate}T12:00:00`,
        };

        startBusyOverlay(
            'Posting fees (challan / AD ledger)',
            'Processing all students for the selected month. Large schools (about 6,000 students) can take several minutes. The server is working — please wait.',
            'challan'
        );
        const api_config = {
            method: 'post',
            url: config.base_url + 'ChallanFeeSystem/StartNewMonthChallan',
            timeout: CHALLAN_POST_TIMEOUT_MS,
            headers: {
                'Content-Type': 'application/json'
            },
            data: data
        };

        axios(api_config)
            .then(function (response) {
                if (response.data.status_code == 1) {
                    toast.success(response.data.status_message || 'Posted to challan / AD ledger.', {
                        position: toast.POSITION.TOP_RIGHT
                    });
                }
                else {
                    toast.error(response.data.status_message || response.data.status_Message || 'Request failed', {
                        position: toast.POSITION.TOP_RIGHT
                    });
                }
            })
            .catch(function (error) {
                const msg = error.code === 'ECONNABORTED'
                    ? 'Request timed out. The server may still be processing — check results before retrying.'
                    : (error.response?.data?.status_message || error.message || 'Network error.');
                toast.error(msg, {
                    position: toast.POSITION.TOP_RIGHT
                });
            })
            .finally(stopBusyOverlay);
    };

    const handleDeletePosting = () => {
        if (!isAdministrator) {
            toast.error('Only administrators can delete fee posting.', { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        const selected = deletablePeriods.find((p) => p.key === deletePeriodKey);
        if (!selected) {
            toast.error('Select a period to delete.', { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        const ok = window.confirm(
            `Permanently delete ALL Monthly challans and linked tblADLedger rows for ${selected.label}?\n\n` +
            'This cannot be undone. The server will refuse if any challan is paid or on a bank scroll.'
        );
        if (!ok) return;

        const data = {
            AccessKey: AccessKey,
            UserID: UserID,
            MemberType: MemberType || 'Admin',
            Group_Name: Group_Name,
            SchoolID: 1,
            SelectedMonth: selected.month,
            FYear: selected.year,
            SessionID: SessionID,
        };

        startBusyOverlay(
            'Deleting fee posting',
            'Removing monthly challan heads, AD ledger lines, and StartedMonth for the selected period…',
            'delete'
        );

        axios({
            method: 'post',
            url: config.base_url + 'ChallanFeeSystem/DeleteMonthlyChallanPosting',
            timeout: CHALLAN_POST_TIMEOUT_MS,
            headers: { 'Content-Type': 'application/json' },
            data,
        })
            .then((response) => {
                if (response.data.status_code === 1) {
                    toast.success(response.data.status_message || 'Posting deleted.', {
                        position: toast.POSITION.TOP_RIGHT,
                    });
                } else {
                    toast.error(response.data.status_message || 'Delete failed.', {
                        position: toast.POSITION.TOP_RIGHT,
                    });
                }
            })
            .catch((error) => {
                const msg = error.code === 'ECONNABORTED'
                    ? 'Request timed out.'
                    : (error.response?.data?.status_message || error.message || 'Network error.');
                toast.error(msg, { position: toast.POSITION.TOP_RIGHT });
            })
            .finally(stopBusyOverlay);
    };

    return (
        <>
            <FeePostingBusyOverlay
                show={isBusy}
                title={busyTitle}
                message={busyDetail}
                elapsedSeconds={elapsedSec}
            />
            <Monthly_navbar />
            <ToastContainer />
            <div className="page-head">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-md-6">
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
                                    <li className="breadcrumb-item"><Link to="/MonthlyTasks/MonthlyTasksDashboard">Monthly Task</Link></li>
                                    <li className="breadcrumb-item active" aria-current="page">Start New Month (Challan)</li>
                                </ol>
                            </nav>
                            <div className="page-title">Start New Month — Challan / AD Ledger</div>
                        </div>
                        <div className="col-md-6">
                            <div className="page-right-actions">
                                <Link to="/MonthlyTasks/ManageMonth" className="button button-white button-icon">
                                    Manage Month
                                    <i className="ri-list-check"></i>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-content" ng-controller="MonthlyTasksController">
                <div className="container">
                    <div className="card mb-2">
                        <div className="card-body p-md-4">
                            <div className="bg-light p-md-4">
                                <p className="mb-1">
                                    <strong>Parallel mode:</strong> This task posts the same class monthly fee structure (M) as the classic Start New Month, but writes <strong>tblChalanHead</strong> and <strong>tblADLedger</strong> only. It does <strong>not</strong> insert into <strong>tblStudentLedger</strong>.
                                </p>
                                <p className="mb-1 text-muted small">
                                    <strong>Post transport fee</strong> adds bus route charges (fee code <strong>005</strong>) like legacy <Link to="/MonthlyTasks/StartNewMonth">Start New Month</Link> — <strong>without</strong> applying the student&apos;s concession to transport. Concession (percentage) applies only to <strong>tuition</strong> lines with fee code <strong>013</strong>. For students whose <strong>previous month</strong> monthly challan was still unpaid, the run adds either <strong>033</strong> late fee (Rs.50) or <strong>008</strong> readmission (Rs.500) on the new challan by inspecting <strong>tblADLedger</strong> on that prior challan (alternates 50 / 500 each month while unpaid; struck-off status remains a manual admin step). After monthly and optional transport lines, <strong>phase 2</strong> runs for those same prior-unpaid cases: each outstanding <strong>tblADLedger</strong> row (fee code + description) is carried into this month&apos;s matching line (Arrears / Receiveable / Pending), or a new row with <strong>Fee_Posted = 0</strong> is added if that fee was not in the new fee structure. Students who already have a Monthly challan for the selected month/year reuse that head for extra lines.
                                </p>
                                <h5><b>Note:</b></h5>
                                <p className="mb-1">Set <strong>Challan due date</strong> below for new monthly heads (you can change month/year first; the date resets to the last day of that month until you adjust it).</p>
                                <p className="mb-0">A full-screen progress indicator runs while posting. Large batches can take several minutes — do not press the button twice or close the tab.</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="container">
                    <div className="card">
                        <div className="card-body p-md-4">
                            <div className="row align-items-center">
                                <div className="col-md-12">
                                    <div className="row align-items-center">
                                        <div className="col-md-2">
                                            <div className="form-group">
                                                <label>Current Month</label>
                                                <select
                                                    className="form-select"
                                                    value={Month}
                                                    onChange={(e) => setMonth(e.target.value)}
                                                    disabled={isBusy}
                                                >
                                                    <option value="">Please Select</option>
                                                    {MONTH_OPTIONS.map((m) => (
                                                        <option key={m.value} value={m.value}>{m.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="form-group">
                                                <label>Current Year</label>
                                                <select
                                                    className="form-select"
                                                    value={fYearsLoaded ? Year : ''}
                                                    onChange={(e) => setYear(e.target.value)}
                                                    disabled={isBusy || !fYearsLoaded}
                                                >
                                                    {!fYearsLoaded ? (
                                                        <option value="">Loading years…</option>
                                                    ) : (
                                                        <>
                                                            <option value="">Please Select</option>
                                                            {fYearList.map((item, index) => (
                                                                <option key={index} value={String(item.FYear)}>{item.FYear}</option>
                                                            ))}
                                                        </>
                                                    )}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label>Challan due date</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={dueDate}
                                                    onChange={(e) => setDueDate(e.target.value)}
                                                    disabled={isBusy}
                                                />
                                                <small className="text-muted d-block mt-1">
                                                    Due / after-due on new challans. Change month or year above to reset default to that month’s last day.
                                                </small>
                                            </div>
                                        </div>
                                        <div className="col-md-5 text-end d-flex align-items-end justify-content-end pb-2">
                                            <button
                                                type="button"
                                                className="button button-primary d-inline-flex align-items-center justify-content-center gap-2"
                                                onClick={() => { scrollToTop(); handleMonth(); }}
                                                disabled={isBusy || !fYearsLoaded || !fYearList.length}
                                                style={{ paddingLeft: '28px', paddingRight: '28px', minWidth: '220px' }}
                                            >
                                                {busyMode === 'challan' ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                                        Processing…
                                                    </>
                                                ) : (
                                                    'Start This Month (Challan)'
                                                )}
                                            </button>
                                        </div>
                                        <div className="col-12">
                                            <div className="form-check border rounded px-3 py-2 bg-light mt-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="snm-post-transport"
                                                    checked={postTransportFee}
                                                    onChange={(e) => setPostTransportFee(e.target.checked)}
                                                    disabled={isBusy}
                                                />
                                                <label className="form-check-label" htmlFor="snm-post-transport">
                                                    <strong>Post transport fee</strong> (bus routes — fee code <strong>005</strong>), same as legacy &quot;Add Transport Fee&quot;. Route charges are added at full route fee; concession is <strong>not</strong> applied to transport (only tuition code <strong>013</strong> receives the percentage concession).
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="container mt-2">
                    <div className="card border-danger">
                        <div className="card-body p-md-4">
                            <h5 className="text-danger mb-2">Delete fee posting (Administrator only)</h5>
                            <p className="text-muted small mb-3">
                                Removes <strong>all</strong> Monthly <strong>tblChalanHead</strong> rows for the chosen period, all linked <strong>tblADLedger</strong> lines, and the matching <strong>StartedMonth</strong> record so the month can be posted again.
                                Only the <strong>current</strong> or <strong>previous</strong> calendar month can be selected. Blocked if any challan is paid or on a bank scroll.
                            </p>
                            {!isAdministrator ? (
                                <p className="mb-0 text-muted">Your account is not in the Administrator group; this action is hidden.</p>
                            ) : (
                                <div className="row align-items-end">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Period to delete</label>
                                            <select
                                                className="form-select"
                                                value={deletePeriodKey}
                                                onChange={(e) => setDeletePeriodKey(e.target.value)}
                                                disabled={isBusy}
                                            >
                                                {deletablePeriods.map((p) => (
                                                    <option key={p.key} value={p.key}>{p.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6 text-md-end mt-3 mt-md-0">
                                        <button
                                            type="button"
                                            className="button button-danger d-inline-flex align-items-center justify-content-center gap-2"
                                            style={{ paddingLeft: '28px', paddingRight: '28px', minWidth: '200px' }}
                                            onClick={() => { scrollToTop(); handleDeletePosting(); }}
                                            disabled={isBusy}
                                        >
                                            {busyMode === 'delete' ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                                    Deleting…
                                                </>
                                            ) : (
                                                'Delete posting'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default StartNewMonthChallan;
